<script setup lang="ts">
  import { CpuChipIcon, UserIcon } from '@heroicons/vue/24/outline'

  interface TranscriptSegment {
    timestamp: string
    speaker: 'bot' | 'caller'
    text: string
  }

  defineProps<{
    transcription: TranscriptSegment[]
    audioElement: HTMLAudioElement | null
  }>()

  const emit = defineEmits<{
    (e: 'seek', time: number): void
  }>()

  const parseTimestamp = (timestamp: string): number | null => {
    const parts = timestamp.split(':')
    if (parts.length !== 2) return null
    const minutes = parseInt(parts[0], 10)
    const seconds = parseInt(parts[1], 10)
    if (isNaN(minutes) || isNaN(seconds)) return null
    return minutes * 60 + seconds
  }

  const seekToTimestamp = (timestamp: string) => {
    const time = parseTimestamp(timestamp)
    if (time === null) return

    emit('seek', time)
  }
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <h3 class="mb-4 text-lg font-semibold text-gray-800">Gemini Transcript</h3>

    <div
      v-if="transcription.length === 0"
      class="py-4 text-center text-sm text-gray-500"
    >
      No transcription available
    </div>

    <div
      v-else
      class="max-h-96 space-y-3 overflow-y-auto"
    >
      <div
        v-for="(segment, index) in transcription"
        :key="index"
        class="flex gap-3"
      >
        <!-- Speaker Icon -->
        <div
          :class="[
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
            segment.speaker === 'bot' ? 'bg-purple-100' : 'bg-blue-100'
          ]"
        >
          <CpuChipIcon
            v-if="segment.speaker === 'bot'"
            class="h-4 w-4 text-purple-600"
          />
          <UserIcon
            v-else
            class="h-4 w-4 text-blue-600"
          />
        </div>

        <!-- Content -->
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <span
              :class="[
                'text-xs font-medium',
                segment.speaker === 'bot' ? 'text-purple-600' : 'text-blue-600'
              ]"
            >
              {{ segment.speaker === 'bot' ? 'Bot' : 'Caller' }}
            </span>
            <button
              v-if="audioElement"
              class="text-xs text-gray-400 hover:text-blue-600 hover:underline"
              :title="`Seek to ${segment.timestamp}`"
              @click="seekToTimestamp(segment.timestamp)"
            >
              [{{ segment.timestamp }}]
            </button>
            <span
              v-else
              class="text-xs text-gray-400"
            >
              [{{ segment.timestamp }}]
            </span>
          </div>
          <p class="mt-1 text-sm text-gray-700">{{ segment.text }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
